import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

// Job email threads (owner 2026-08-17, #50-C) — read-only, fully automatic. Groups every email the
// job's business produced: job-number correspondence (status / assignment / workflow-step emails and
// their replies), the source quotation/invoice's thread, and each receipt's thread. Classification
// happens server-side from signals maintained as mail arrives — nothing to file by hand. Message
// rows mirror PropertyTaxEmailThread (direction chip · from → to · date · expandable body).

type ThreadEmail = { id: string; direction: 'sent' | 'received'; fromName: string; fromEmail: string; to: string[]; date: number; subject: string; bodyHtml: string };
type Group = { key: string; label: string; subject: string; emails: ThreadEmail[] };

const fmtDate = (sec: number) => {
  if (!sec) return '';
  const d = new Date(sec * 1000); const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function JobEmailThreads({ jobId }: { jobId: string }) {
  const [open, setOpen] = useState(false);          // whole section — lazy: nothing loads until expanded
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openMail, setOpenMail] = useState<string | null>(null);

  // Switching to another job must drop the previous job's threads (review 2026-08-17).
  useEffect(() => { setGroups(null); setOpen(false); setOpenGroup(null); setOpenMail(null); setErr(''); }, [jobId]);
  useEffect(() => {
    if (!open || groups || loading) return;
    let alive = true;
    setLoading(true); setErr('');
    api.jobEmailThreads(jobId)
      .then((d: any) => { if (!alive) return; setGroups(d.groups || []); if ((d.groups || []).length) setOpenGroup(d.groups[0].key); })
      .catch((e: any) => alive && setErr(e?.message || 'Failed to load'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, jobId]); // eslint-disable-line

  const total = (groups || []).reduce((n, g) => n + g.emails.length, 0);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-app)', marginTop: 10 }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer' }}>
        <i className="ti ti-mail" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' }}>
          Email threads {groups ? `— ${total} email(s) in ${groups.length} thread(s)` : ''}
        </span>
        <span style={{ flex: 1 }} />
        <i className={`ti ti-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
              <i className="ti ti-loader" style={{ fontSize: 20, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite', display: 'inline-block' }} />
            </div>
          ) : err ? (
            <div style={{ fontSize: 13, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '10px 12px' }}>{err}</div>
          ) : !groups || groups.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 20, fontSize: 13 }}>
              <i className="ti ti-mail-off" style={{ fontSize: 22, display: 'block', marginBottom: 6 }} />
              No emails found for this job yet.
            </div>
          ) : groups.map(g => {
            const gOpen = openGroup === g.key;
            const last = g.emails[g.emails.length - 1];
            return (
              <div key={g.key} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8, overflow: 'hidden', background: 'var(--bg-surface)' }}>
                <div onClick={() => setOpenGroup(gOpen ? null : g.key)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', background: gOpen ? 'var(--bg-active)' : 'transparent' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: '#6D28D9', background: '#F3EEFF', borderRadius: 6, padding: '2px 8px', flexShrink: 0 }}>{g.label}</span>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {g.subject || last?.subject || ''}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>{g.emails.length} · {fmtDate(last?.date || 0)}</span>
                  <i className={`ti ti-chevron-${gOpen ? 'up' : 'down'}`} style={{ fontSize: 13, color: 'var(--text-tertiary)', flexShrink: 0 }} />
                </div>
                {gOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '8px 10px' }}>
                    {g.emails.map(e => {
                      const mOpen = openMail === `${g.key}|${e.id}`;
                      const sent = e.direction === 'sent';
                      return (
                        <div key={e.id} style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6, overflow: 'hidden' }}>
                          <div onClick={() => setOpenMail(mOpen ? null : `${g.key}|${e.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer', background: mOpen ? 'var(--bg-active)' : 'transparent' }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: sent ? '#15803D' : '#1D4ED8', background: sent ? '#E7F6EE' : '#E6F0FE', borderRadius: 5, padding: '1px 7px', flexShrink: 0 }}>{sent ? 'SENT' : 'RECEIVED'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.fromName || e.fromEmail}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.fromEmail}{e.to?.length ? ` → ${e.to.join(', ')}` : ''}</div>
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtDate(e.date)}</span>
                            <i className={`ti ti-chevron-${mOpen ? 'up' : 'down'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }} />
                          </div>
                          {mOpen && (
                            <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', background: '#fff' }}>
                              <div className="rte-render" style={{ fontSize: 13, lineHeight: 1.6, color: '#1F2937', maxHeight: 340, overflowY: 'auto', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: e.bodyHtml || '<i>(no content)</i>' }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
